import React, { useEffect, useRef, useState } from 'react';
import './Media.css';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchMedia, uploadMedia, deleteMedia } from '../store/slices/mediaSlice';
import { apiService } from '../services/api';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import ImageBroadcastModal from '../components/ImageBroadcastModal';

// Uppy imports
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import ImageEditor from '@uppy/image-editor';
import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import '@uppy/image-editor/css/style.min.css';
const SERVER_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api\/v1\/?$/, '');

const getMediaType = (mimeType) => {
  if (!mimeType) return 'HEADER_IMAGE';
  if (mimeType.startsWith('video/')) return 'HEADER_VIDEO';
  if (mimeType.startsWith('image/')) return 'HEADER_IMAGE';
  return 'HEADER_DOCUMENT';
};

const Media = () => {
  const dispatch = useAppDispatch();
  const { items, loading } = useAppSelector(state => state.media);
  const dashboardRef = useRef(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null, isLoading: false });
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);
  const [samplePhoneNumber, setSamplePhoneNumber] = useState('');
  const [sampleSending, setSampleSending] = useState(false);
  const [sampleStatus, setSampleStatus] = useState(null);
  const [sampleError, setSampleError] = useState(null);

  useEffect(() => {
    dispatch(fetchMedia());
  }, [dispatch]);

  useEffect(() => {
    // Create Uppy instance
    const uppy = new Uppy({
      restrictions: {
        maxFileSize: 5 * 1024 * 1024, // 5MB
        allowedFileTypes: ['image/*', 'video/*']
      },
      autoProceed: false
    });

    const onComplete = (result) => {
      if (result.successful && result.successful.length > 0) {
        const uploads = result.successful.map((f) => {
          const fileObj = uppy.getFile(f.id);
          if (fileObj && fileObj.data) {
            const type = getMediaType(fileObj.data.type);
            return dispatch(uploadMedia({ file: fileObj.data, type })).unwrap();
          }
          return Promise.resolve();
        });

        Promise.allSettled(uploads).then(() => {
          // Clear all files from the dashboard after uploads finish
          uppy.cancelAll();
        });
      }
    };

    uppy.on('complete', onComplete);

    // Mount Dashboard to DOM ref after render (same pattern as TemplateModal)
    const timer = setTimeout(() => {
      if (dashboardRef.current) {
        if (!uppy.getPlugin('ImageEditor')) {
          uppy.use(ImageEditor, { quality: 0.9 });
        }
        if (!uppy.getPlugin('Dashboard')) {
          uppy.use(Dashboard, {
            target: dashboardRef.current,
            inline: true,
            width: '100%',
            height: 380,
            showProgressDetails: true,
            proudlyDisplayPoweredByUppy: false,
            note: 'Images & videos up to 5MB. Click the edit icon to crop or adjust before uploading.',
            plugins: ['ImageEditor']
          });
        }
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      uppy.off('complete', onComplete);
      try {
        if (typeof uppy.close === 'function') {
          uppy.close();
        } else {
          if (typeof uppy.cancelAll === 'function') uppy.cancelAll();
          if (typeof uppy.reset === 'function') uppy.reset();
        }
      } catch (err) {
        console.warn('Error while closing Uppy instance', err);
      }
    };
  }, [dispatch]);

  const handleDelete = (media) => {
    setDeleteModal({ isOpen: true, item: media, isLoading: false });
  };

  const toggleImageSelect = (id) => {
    setSelectedImageId(prev => (prev === id ? null : id));
  };

  const handleClearSelection = () => setSelectedImageId(null);

  const handleSendImages = () => setIsBroadcastModalOpen(true);

  const selectedMediaItem = items.find(i => (i.id || i.filename) === selectedImageId);
  const selectedMediaItems = selectedMediaItem ? [selectedMediaItem] : [];

  const handleSendSampleImage = async () => {
    if (!samplePhoneNumber.trim()) {
      setSampleError('Please enter a phone number to send the sample image.');
      setSampleStatus(null);
      return;
    }

    setSampleSending(true);
    setSampleError(null);
    setSampleStatus(null);

    const imageUrl = selectedMediaItems[0]?.url || selectedMediaItems[0]?.mediaUrl || '';

    try {
      await apiService.media.sendSample({
        toPhoneNumber: samplePhoneNumber.trim(),
        imageUrl
      });
      setSampleStatus('Sample image sent successfully.');
      setSamplePhoneNumber('');
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to send sample image.';
      setSampleError(message);
    } finally {
      setSampleSending(false);
    }
  };

  const handleConfirmDelete = () => {
    setDeleteModal(prev => ({ ...prev, isLoading: true }));
    dispatch(deleteMedia(deleteModal.item.id))
      .unwrap()
      .then(() => setDeleteModal({ isOpen: false, item: null, isLoading: false }))
      .catch(err => {
        console.error('Delete failed:', err);
        setDeleteModal(prev => ({ ...prev, isLoading: false }));
      });
  };

  return (
    <div className="media-container">
      <div className="media-header">
        <div>
          <h1>Media Library</h1>
          <p>Upload and manage media for WhatsApp messages. Drag & drop files or click to browse.</p>
        </div>
      </div>

      <div className="media-sample-panel">
        <div className="media-sample-panel-row">
          <input
            type="tel"
            className="media-sample-input"
            placeholder="Sample recipient phone number"
            value={samplePhoneNumber}
            onChange={(e) => setSamplePhoneNumber(e.target.value)}
          />
          <button
            className="media-sel-btn media-sel-btn--primary"
            onClick={handleSendSampleImage}
            disabled={sampleSending || !samplePhoneNumber.trim()}
          >
            {sampleSending ? 'Sending sample…' : 'Send Sample Image'}
          </button>
        </div>
        <div className="media-sample-hint-row">
          <span className="media-sample-hint">
            {selectedMediaItems.length > 0
              ? 'Selected first image will be sent if available, otherwise the default sample image will be used.'
              : 'No image selected: the default sample image will be sent.'}
          </span>
        </div>
        {sampleStatus && <div className="media-sample-status media-sample-status--success">{sampleStatus}</div>}
        {sampleError && <div className="media-sample-status media-sample-status--error">{sampleError}</div>}
      </div>

      {/* Upload Section */}
      <div className="media-upload-section">
        <div ref={dashboardRef}></div>
      </div>

      {/* Media Grid */}
      <div className="media-section">
        <div className="section-header">
          <h2>Your Media ({items.length})</h2>
        </div>

        <div className="media-grid">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading media...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🖼️</div>
              <h3>No media uploaded yet</h3>
              <p>Upload images or videos to use them in templates and messages.</p>
            </div>
          ) : (
            items.map(item => {
              const itemMime = item.mime || item.mimeType || '';
              const isImage = itemMime.startsWith('image');
              const itemId = item.id || item.filename || item._id;
              const isSelected = selectedImageId === itemId;
              return (
                <div
                  className={`media-card${isSelected ? ' media-card--selected' : ''}`}
                  key={item.id}
                  onClick={() => isImage && toggleImageSelect(item.id)}
                  style={isImage ? { cursor: 'pointer' } : {}}
                >
                  {isImage && (
                    <div className="media-select-checkbox" onClick={e => { e.stopPropagation(); toggleImageSelect(item.id); }}>
                      <input type="checkbox" checked={isSelected} readOnly />
                    </div>
                  )}
                  {isSelected && <div className="media-select-order">1</div>}
                  <div className="media-preview">
                    {isImage ? (
                      <img src={(item.url || item.mediaUrl) ? SERVER_BASE_URL + (item.url || item.mediaUrl) : ''} alt={item.filename || item.id} />
                    ) : (
                      <div className="media-placeholder">
                        <span>📹</span>
                        <small>{item.mime || 'file'}</small>
                      </div>
                    )}
                  </div>
                  <div className="media-info">
                    <p className="media-name" title={item.filename}>{item.filename || item.id}</p>
                    <small className="media-size">{item.size ? (item.size / 1024).toFixed(1) + ' KB' : '—'}</small>
                  </div>
                  <div className="media-actions">
                    <button
                      className="delete-btn"
                      onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                      title="Delete media"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, item: null, isLoading: false })}
        onConfirm={handleConfirmDelete}
        customerName={deleteModal.item?.filename || deleteModal.item?.id}
        type="media"
        isLoading={deleteModal.isLoading}
      />

      {/* Selection action bar */}
      {selectedImageId && (
        <div className="media-selection-bar">
          <div className="media-selection-top">
            <div className="media-selection-info">
              <span className="media-selection-count">1 image selected</span>
              <span className="media-selection-hint">Selected image will be sent via IMAGE_HAWK template (if approved)</span>
            </div>
            <div className="media-selection-actions">
              <button className="media-sel-btn media-sel-btn--secondary" onClick={handleClearSelection}>
                Clear Selection
              </button>
              <button className="media-sel-btn media-sel-btn--secondary" onClick={handleSendSampleImage} disabled={sampleSending || !samplePhoneNumber.trim()}>
                {sampleSending ? 'Sending...' : 'Send Sample Image'}
              </button>
              <button className="media-sel-btn media-sel-btn--primary" onClick={handleSendImages}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                Send to Recipients
              </button>
            </div>
          </div>

          <div className="media-selection-bottom">
            <input
              type="tel"
              className="media-sample-input"
              placeholder="Recipient phone number"
              value={samplePhoneNumber}
              onChange={(e) => setSamplePhoneNumber(e.target.value)}
            />
            <span className="media-sample-hint">
              {selectedMediaItems.length > 0
                ? 'Selected first image will be sent as a single sample.'
                : 'No image selected: default sample image will be sent.'}
            </span>
          </div>

          {sampleStatus && <div className="media-sample-status media-sample-status--success">{sampleStatus}</div>}
          {sampleError && <div className="media-sample-status media-sample-status--error">{sampleError}</div>}
        </div>
      )}

      {isBroadcastModalOpen && (
        <ImageBroadcastModal
          isOpen={isBroadcastModalOpen}
          onClose={() => setIsBroadcastModalOpen(false)}
          selectedImages={selectedMediaItems}
          serverBaseUrl={SERVER_BASE_URL}
        />
      )}
    </div>
  );
};

export default Media;